<template>
  <div class="page-content">
    <h1>{{ $t('v.settings.title')}}</h1>

    <v-card>
      <!-- NOTIFICATIONS SECTION -->
      <v-card-section>
        <v-card-title>{{ titleNotification }}</v-card-title>
        <v-card-body v-loading="loading">
          <hr />
          <div class="setting">
            <div class="setting__description">
              <div class="setting__preview">
                <span class="setting__label">{{ $t('v.settings.labelEmail') }}</span>
                <span class="setting__value">{{ emailsString }}</span>
              </div>
              <div class="setting__action">
                <v-button
                  theme="primary"
                  :disabled="!canChangeSettings"
                  view="outline"
                  @click="$refs.emailsDialogChange.open()"
                >{{ $t('v.settings.changeEmail') }}</v-button>
              </div>
            </div>
            <div class="setting__control">
              <v-switch-with-popover
                :title="hasEnableNotificationsTitle"
                :value="hasEnableNotifications"
                :disabled="!canChangeSettings"
                @change="changeAccountSetting('hasEnableNotifications', $event)"
              />
            </div>
          </div>
          <hr />
          <div class="setting">
            <div class="setting__description">
              <div class="setting__preview">
                <div class="setting__label">{{ $t('v.settings.labelGarbageLimit') }}</div>
                <v-input-number
                  class="setting__input"
                  ref="garbageInput"
                  :value="junkNotificationLimit"
                  :disabled="!canChangeSettings"
                  @change="junkNotificationLimit = $event"
                />
                <div v-if="currency" class="setting__label">{{ currency }}</div>
              </div>
              <div class="setting__action">
                <v-button
                  theme="primary"
                  view="outline"
                  :disabled="!canChangeSettings"
                  @click="changeAccountSetting('junkNotificationLimit', junkNotificationLimit)"
                >{{ $t('v.settings.saveGarbageLimit') }}</v-button>
              </div>
            </div>
            <div class="setting__control">
              <v-switch-with-popover
                :title="hasJunkNotificationLimitTitle"
                :value="hasJunkNotificationLimit"
                :disabled="!canChangeSettings"
                @change="changeAccountSetting('hasJunkNotificationLimit', $event)"
              />
            </div>
          </div>
          <hr />
        </v-card-body>
      </v-card-section>
      <!-- PLACEMENT BAN CRITERIA -->
      <v-card-section>
        <v-card-title>{{titlePlacementBanCriteria}}</v-card-title>
        <v-card-body v-loading="loading">
          <hr />
          <div class="setting">
            <div
              class="setting__label"
            >{{ $t('v.settings.labelBadPerformanceCriteriaForPlacements') }}</div>
            <div class="setting__control">
              <v-switch-with-popover
                :value="hasPerformanceClassification"
                :title="hasPerformanceClassificationTitle"
                :disabled="!canChangeSettings"
                @change="changeAccountSetting('hasPerformanceClassification', $event)"
              />
            </div>
          </div>
          <hr />
          <div class="setting">
            <div class="setting__label">{{ $t('v.settings.labelGlobalBlackList') }}</div>
            <div class="setting__control">
              <v-switch-with-popover
                :value="hasGlobalBlacklistClassification"
                :title="hasGlobalBlacklistClassificationTitle"
                :disabled="!canChangeSettings"
                @change="changeAccountSetting('hasGlobalBlacklistClassification', $event)"
              />
            </div>
          </div>
          <hr />
          <div class="setting">
            <div class="setting__label">{{ $t('v.settings.labelGarbageDomains') }}</div>
            <div class="setting__control">
              <v-switch-with-popover
                :value="hasDnsClassification"
                :title="hasDnsClassificationTitle"
                :disabled="!canChangeSettings"
                @change="changeAccountSetting('hasDnsClassification', $event)"
              />
            </div>
          </div>
          <hr />
          <div class="setting">
            <div class="setting__label">{{ $t('v.settings.labelHighCtrCriteria') }}</div>
            <div class="setting__control">
              <v-switch-with-popover
                :value="hasCtrClassification"
                :title="hasCtrClassificationTitle"
                :disabled="!canChangeSettings"
                @change="changeAccountSetting('hasCtrClassification', $event)"
              />
            </div>
          </div>
          <hr />
          <div class="setting">
            <div class="setting__description">
              <div class="setting__preview">
                <div class="setting__label">{{ $t('v.settings.labelBanByMask') }}</div>
                <span class="setting__value">{{ masksString }}</span>
              </div>
              <div class="setting__action">
                <v-button
                  theme="primary"
                  :disabled="!canChangeSettings"
                  view="outline"
                  @click="$refs.banByMaskChange.open()"
                >{{ $t('v.settings.changeMasks') }}</v-button>
              </div>
            </div>
            <div class="setting__control">
              <v-switch-with-popover
                :value="hasMaskClassification"
                :title="hasMaskClassificationTitle"
                :disabled="!canChangeSettings"
                @change="changeAccountSetting('hasMaskClassification', $event)"
              />
            </div>
          </div>
          <hr />
        </v-card-body>
      </v-card-section>
      <!-- ADDITIONAL PLACEMENT SETTINGS -->
      <v-card-section>
        <v-card-title>{{titleAdditionalPlacementSettings}}</v-card-title>
        <v-card-body v-loading="loading">
          <hr />
          <div class="setting">
            <div class="setting__label">{{ $t('v.settings.labelExternalPlacements') }}</div>
            <div class="setting__control">
              <v-switch-with-popover
                :value="hasNetworksBanned"
                :title="hasNetworksBannedTitle"
                :disabled="!canChangeSettings"
                @change="changeAccountSetting('hasNetworksBanned', $event)"
              />
            </div>
          </div>
          <hr />
        </v-card-body>
      </v-card-section>
      <!-- AUTOMATIC MODE -->
      <v-card-section>
        <v-card-title>{{ titleAutomaticMode }}</v-card-title>
        <v-card-body v-loading="loading">
          <hr />
          <div class="setting">
            <div class="setting__label">{{ $t('v.settings.labelPlacements') }}</div>
            <div class="setting__control">
              <v-switch-with-popover
                :value="hasPlacementsAutodisable"
                :title="hasPlacementsAutodisableTitle"
                :disabled="!canChangeSettings"
                @change="confirmSettingsChange('hasPlacementsAutodisable', $event)"
              />
            </div>
          </div>
        </v-card-body>
      </v-card-section>
    </v-card>

    <!-- dialogs -->
    <v-emails-dialog-change
      v-if="canChangeSettings"
      :emails="emails"
      :loading="loading"
      ref="emailsDialogChange"
    />
    <v-masks-dialog-change
      v-if="canChangeSettings"
      :masks="masks"
      :loading="loading"
      ref="banByMaskChange"
    />
    <v-confirmation-dialog
      ref="firstBanConfirmationDialog"
      :title="$t('c.dialog.titleFirstBan')"
      :message="$t('c.dialog.messageFirstBan')"
      @confirm="changeAccountSetting('hasPlacementsAutodisable', true)"
    />
  </div>
</template>

<script>
import VInputNumber from '@/components/common/VInputNumber.vue'
import VConfirmationDialog from '@/components/common/VConfirmationDialog.vue'
import VEmailsDialogChange from '@/components/common/VEmailsDialogChange.vue'
import VMasksDialogChange from '@/components/common/VMasksDialogChange.vue'
import VSwitchWithPopover from '@/components/common/VSwitchWithPopover.vue'
import VCard from '@/components/common/VCard.vue'
import VCardTitle from '@/components/common/VCardTitle.vue'
import VCardBody from '@/components/common/VCardBody.vue'
import VCardSection from '@/components/common/VCardSection.vue'
import * as types from '@/store/types'
import * as authorities from '@/utils/authorities'
import storeUtils from '@/mixins/storeUtils'

export default {
  name: 'Settings',

  components: {
    VInputNumber,
    VConfirmationDialog,
    VEmailsDialogChange,
    VMasksDialogChange,
    VSwitchWithPopover,
    VCard,
    VCardSection,
    VCardBody,
    VCardTitle
  },

  mixins: [storeUtils],

  computed: {
    loading() {
      return this.$store.state[types.ACCOUNT_SETTINGS].loading
    },
    model() {
      return this.$store.state[types.ACCOUNT_SETTINGS].model
    },
    canChangeSettings() {
      return this.$store.getters.hasAuthorities(authorities.CHANGE_SETTINGS)
    },

    // section Notifications
    titleNotification() {
      return this.$t('v.settings.titleNotification').toUpperCase()
    },

    hasEnableNotifications() {
      return this.model.hasEnableNotifications
    },
    hasEnableNotificationsTitle() {
      const actionTextEnable = this.$t('v.settings.popoverTitleEnableEmails')
      const actionTextDisable = this.$t('v.settings.popoverTitleDisableEmails')
      return this.hasEnableNotifications ? actionTextDisable : actionTextEnable
    },
    emails() {
      return this.$store.getters.notificationEmails
    },
    emailsString() {
      return this.emails.map(item => item.email).join(', ')
    },
    hasJunkNotificationLimit() {
      return this.model.hasJunkNotificationLimit
    },
    hasJunkNotificationLimitTitle() {
      const actionTextEnable = this.$t(
        'v.settings.popoverTitleEnableGarbageLimit'
      )
      const actionTextDisable = this.$t(
        'v.settings.popoverTitleDisableGarbageLimit'
      )
      return this.hasJunkNotificationLimit
        ? actionTextDisable
        : actionTextEnable
    },
    currency() {
      const { currencyCode } = this.model

      if (typeof currencyCode === 'string' && currencyCode !== '') {
        const currency = currencyCode === 'RUB'
          ? this.$t('currency.RUB')
          : currencyCode.toLowerCase()

        return `${currency}.`
      }

      return this.$t('currency.RUB')
    },
    junkNotificationLimit: {
      get() {
        return this.model.junkNotificationLimit
      },
      set(value) {
        this.updateModel(types.ACCOUNT_SETTINGS, {
          junkNotificationLimit: value
        })
      }
    },

    // section Placements Ban criteria
    titlePlacementBanCriteria() {
      return this.$t('v.settings.titlePlacementBanCriteria').toUpperCase()
    },
    hasPerformanceClassification() {
      return this.model.hasPerformanceClassification
    },
    hasPerformanceClassificationTitle() {
      const turnOn = this.$t('v.settings.popoverEnableThisCriteria')
      const turnOff = this.$t('v.settings.popoverDisableThisCriteria')
      return this.hasPerformanceClassification ? turnOff : turnOn
    },
    hasGlobalBlacklistClassification() {
      return this.model.hasGlobalBlacklistClassification
    },
    hasGlobalBlacklistClassificationTitle() {
      const turnOn = this.$t('v.settings.popoverEnableThisCriteria')
      const turnOff = this.$t('v.settings.popoverDisableThisCriteria')
      return this.hasGlobalBlacklistClassification ? turnOff : turnOn
    },
    hasDnsClassification() {
      return this.model.hasDnsClassification
    },
    hasDnsClassificationTitle() {
      const turnOn = this.$t('v.settings.popoverEnableThisCriteria')
      const turnOff = this.$t('v.settings.popoverDisableThisCriteria')
      return this.hasDnsClassification ? turnOff : turnOn
    },
    hasCtrClassification() {
      return this.model.hasCtrClassification
    },
    hasCtrClassificationTitle() {
      const turnOn = this.$t('v.settings.popoverEnableThisCriteria')
      const turnOff = this.$t('v.settings.popoverDisableThisCriteria')
      return this.hasCtrClassification ? turnOff : turnOn
    },
    masks() {
      return this.model.masks || []
    },
    masksString() {
      return this.masks.map(mask => mask.mask).join(', ')
    },
    hasMaskClassification() {
      return this.model.hasMaskClassification
    },
    hasMaskClassificationTitle() {
      const turnOn = this.$t('v.settings.popoverEnableThisCriteria')
      const turnOff = this.$t('v.settings.popoverDisableThisCriteria')
      return this.hasMaskClassification ? turnOff : turnOn
    },

    // section Additional Settings
    titleAdditionalPlacementSettings() {
      return this.$t(
        'v.settings.titleAdditionalPlacementSettings'
      ).toUpperCase()
    },
    hasNetworksBanned() {
      return this.model.hasNetworksBanned
    },
    hasNetworksBannedTitle() {
      const actionTextEnable = this.$t(
        'v.settings.popoverTitleEnableExternalPlacements'
      )
      const actionTextDisable = this.$t(
        'v.settings.popoverTitleDisableExternalPlacements'
      )
      return this.hasNetworksBanned ? actionTextEnable : actionTextDisable
    },

    // section Automatic Mode
    titleAutomaticMode() {
      return this.$t('v.settings.titleAutomaticMode').toUpperCase()
    },
    hasPlacementsAutodisable() {
      return this.model.hasPlacementsAutodisable
    },
    hasPlacementsAutodisableTitle() {
      const actionTextEnable = this.$t(
        'v.settings.popoverTitleEnableStoppingPlacements'
      )
      const actionTextDisable = this.$t(
        'v.settings.popoverTitleDisableStoppingPlacements'
      )
      return this.hasPlacementsAutodisable
        ? actionTextDisable
        : actionTextEnable
    },
    isFirstBan() {
      return this.$store.state[types.SESSION].model.user.isFirstBan
    }
  },

  created() {
    this.$store.dispatch(types.GET_ACCOUNT_SETTINGS)
  },

  methods: {
    confirmSettingsChange(setting, value) {
      if (setting === 'hasPlacementsAutodisable') {
        if (this.isFirstBan && value) {
          this.$refs.firstBanConfirmationDialog.open()
        } else {
          this.changeAccountSetting(setting, value)
        }
      }
    },
    changeAccountSetting(setting, value) {
      this.$store
        .dispatch(types.CHANGE_ACCOUNT_SETTING, { setting, value })
        .then(() => {
          if (value) {
            if (setting === 'hasPlacementsAutodisable') {
              this.$gtag.event('autodisable_placements_enabled')
            }
          }
        })
    }
  }
}
</script>
